<template>
  <div
    class="rs-select"
    :class="inputClasses"
  >
    <label v-if="label">{{ label }}<sup v-if="required">*</sup></label>
    <span v-if="helpTooltip">
      <RsIcon
        :id="`helperTooltip-${id}`"
        icon="question-circle"
        class="tooltip-question-icon"
      />
      <RsTooltip
        :target="`helperTooltip-${id}`"
        :title="helpTooltip"
        :position="helpTooltipPosition"
        :delay="{ show: 200 }"
      />
    </span>
    <ValidationProvider
      v-slot="{ errors, valid }"
      class="veeValidate"
      tag="div"
      :name="validationName"
      :mode="validationMode"
      :rules="rules"
    >
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>
          This field is managed by <strong>[Data Source]</strong>.<br>Changing
          this will be temporary.
        </p>
      </div>
      <BInputGroup>
        <BInputGroupPrepend
          v-if="imported"
          @click="toggleState"
          @mouseover="importHover = true"
          @mouseleave="importHover = false"
        >
          <span class="input-group-text">
            <RsIcon :icon="isUnlocked ? 'lock-open-alt' : 'lock-alt'" />
          </span>
        </BInputGroupPrepend>
        <BInputGroupPrepend v-if="icon && !imported">
          <span class="input-group-icon">
            <RsIcon
              :icon="icon"
              weight="far"
            />
          </span>
        </BInputGroupPrepend>
        <BDropdown
          v-if="isOptionsWithIcons"
          :id="id"
          :text="showSelectedIcon ? undefined : selectedOptionText"
          :disabled="disabled || (imported && isUnlocked === false)"
          class="rs-select-dropdown"
          :class="{
            'border-radius-right-curved': imported || icon,
            'border-radius-curved': !imported && !icon,
          }"
          variant="transparent"
          block
        >
          <template
            v-if="showSelectedIcon"
            #button-content
          >
            <span class="d-flex align-items-center rs-select-btn-content">
              <RsIcon
                v-if="selectedOption?.icon"
                :icon="selectedOption.icon"
                :weight="selectedOption.iconWeight"
                :icon-class="selectedOption.iconClass"
                class="mr-2"
              />
              <span class="rs-select-btn-text">{{ selectedOptionText }}</span>
            </span>
          </template>
          <template>
            <span v-for="(option, index) in optionsArray" :key="`option-${index}`">
              <BDropdownDivider v-if="option.divider" />
              <BDropdownItem
                :active="option.value === value"
                @click="$emit('input', option.value)"
              >
                <RsIcon
                  v-if="option.icon"
                  :icon="option.icon"
                  :weight="option.iconWeight"
                  :icon-class="option.iconClass"
                  class="mr-2"
                />
                <span>{{ option.text }}</span>
              </BDropdownItem>
            </span>
          </template>
        </BDropdown>
        <BFormSelect
          v-if="!isOptionsWithIcons"
          :id="id"
          :class="{
            'border-radius-right-curved': imported || icon,
            'border-radius-curved': !imported && !icon,
          }"
          :value="getValueType(value, emptyValue)"
          :disabled="disabled || (imported && isUnlocked === false)"
          :options="options"
          :state="getStateValidation(errors, valid)"
          @change="$emit('input', $event)"
        />
      </BInputGroup>
      <div
        v-if="errors.length && !valid"
        class="text--error"
      >
        {{ errors[0] }}
      </div>
    </ValidationProvider>
  </div>
</template>
<script lang="ts">
/* eslint-disable camelcase */
import { ValidationProvider, extend } from 'vee-validate';
import { is_not, min, min_value, required } from 'vee-validate/dist/rules';
import { defineComponent, PropType } from 'vue';
import { BInputGroup, BDropdown, BDropdownItem, BDropdownDivider } from 'bootstrap-vue';
import i18n from '@/i18n';
import { TRsFormSelectOptions } from '@/shared/types/t-rs-form-select-options';

extend('min', min);
extend('min_value', min_value);
extend('required', { ...required, message: () => i18n.t('validation.fieldRequired') as string });
extend('is_not', is_not);

type TSelectValue = string | number | boolean | Record<string, unknown> | null | undefined;
type TEmptyValue = string | number | boolean | null | undefined;

export default defineComponent({
  name: 'RsFormSelect',
  components: {
    ValidationProvider,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    name: String,
    label: String,
    options: [Array, Object],
    required: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    filterName: String,
    value: [String, Number, Object, Boolean] as PropType<TSelectValue>,
    /**
     *  Used to create a read-only input with the option to edit for imported fields.
    */
    imported: {
      type: Boolean,
      default: false,
    },
    emptyValue: {
      type: [String, Number, Boolean] as PropType<TEmptyValue>,
      default: undefined,
    },
    helpTooltip: {
      type: String,
      default: '',
    },
    helpTooltipPosition: {
      type: String,
      default: 'right',
      validator: (value: string) => ['top', 'right', 'bottom', 'left'].includes(value),
    },
    useNameForValidation: {
      type: Boolean,
    },
    icon: String,
    /**
     * Defines how form field validation is triggered within the `ValidationProvider`.
     * Determines which user interactions (e.g., input, blur, or change events)
     * will cause the field to be validated.
     *
     * @see {@link https://vee-validate.logaretm.com/v2/guide/interaction.html }
     */
    validationMode: {
      type: String,
      default: 'aggressive',
      validator: (value: string) => ['aggressive', 'eager', 'passive', 'lazy'].includes(value),
    },
    /**
     * When true, displays the menu group that has the optional icon display on menu items
     */
    isOptionsWithIcons: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, displays the selected option's icon in the dropdown toggle button.
     */
    showSelectedIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isUnlocked: false as boolean,
      importHover: false as boolean,
    };
  },
  computed: {
    inputClasses(): Record<string, boolean> {
      return {
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
      };
    },
    validationName(): string {
      if (this.useNameForValidation && this.name) {
        return this.name;
      }

      return this.label || 'field';
    },
    /**
     * Normalizes options to an array of { value, text, icon?, divider? } objects.
     */
    optionsArray(): Array<TRsFormSelectOptions> {
      if (!this.options || !Array.isArray(this.options)) {
        return [];
      }
      return this.options.map((opt: unknown) => {
        if (typeof opt === 'object' && opt !== null) {
          const o = opt as Record<string, unknown>;
          return {
            value: o.value,
            text: (o.text as string) || String(o.value),
            icon: o.icon as string | undefined,
            iconWeight: o.iconWeight as string | undefined,
            iconClass: o.iconClass as string | undefined,
            divider: o.divider as boolean | undefined,
          };
        }
        return { value: opt, text: String(opt) };
      });
    },
    /**
     * Returns the currently selected option object.
     */
    selectedOption(): TRsFormSelectOptions | undefined {
      return this.optionsArray.find((opt) => opt.value === this.value);
    },
    /**
     * Returns the text of the currently selected option for display in the dropdown toggle.
     */
    selectedOptionText(): string {
      return this.selectedOption ? this.selectedOption.text : '';
    },
  },
  methods: {
    /**
     * Sets the state to is-valid/is-invalid or none if not required.
     * @param errors Array of string errors
     * @param valid Boolean form validation
    */
    getStateValidation(errors: string[], valid: boolean): boolean | null {
      if (!this.required) {
        return null;
      }
      if (valid && !this.disabled) {
        return true;
      }
      if (errors.length) {
        return false;
      }
      return null;
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
    getValueType(value: TSelectValue, emptyValue: TEmptyValue) {
      if (emptyValue === undefined && emptyValue !== null) {
        return (value || value === 0 || value === false ? value : '');
      }

      if (emptyValue !== value) {
        return value;
      }

      return emptyValue;
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

// Override Bootstrap is-valid style

.rs-select {
  position: relative;
  margin-bottom: 1rem;

  label {
    display: inline-block;
    margin-bottom: 0.5rem;
    font-size: $form-label-font-size;
    font-weight: $form-label-font-weight;
  }
  select {
    width: 100%;
    height: 40px;
    line-height: $input-line-height;
    border-radius: $input-radius;
    border: $input-border;
    font-size: $input-font-size;
    color: $input-color;
    padding: 0.5rem 1.5rem 0.5rem 0.5rem;
  }
  .custom-select {
    background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='gray' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E");
    &.is-valid {
      // Override bootstrap style
      background: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='gray' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") right .75rem center/8px 10px no-repeat;
      border-color: #c9ccdf;
      &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgb(0 123 255 / 25%);
      }
    }
  }
  .custom-select:disabled {
    color: #0f0f0f;
    opacity: 1;
  }
  .border-radius-right-curved {
    border-radius: 0 6px 6px 0!important;
  }
  .border-radius-curved {
    border-radius: $input-radius!important;
  }
  option {
    background-color: $white;
    box-sizing: border-box;
    &:focus {
      border-color: $info;
    }
  }
  .input-group-icon {
    padding: 0.375rem 0.65rem;
    color: rgba($tertiary-muted, 0.4);
    background-color: $white;
    border: solid 1px #C9CCDF;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .rs-select-dropdown {
    width: 100%;

    .btn {
      width: 100%;
      height: calc(1.5em + 0.75rem + 2px);
      padding: 0.375rem 0.75rem;
      text-align: left;
      background-color: $white;
      border: 1px solid #c9ccdf;
      border-radius: $input-radius;
      font-size: $input-font-size;
      font-weight: 400;
      line-height: 1.5;
      color: $input-color;
      display: flex;
      align-items: center;
      justify-content: space-between;
      overflow: hidden;
      background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='gray' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.75rem center;
      background-size: 8px 10px;
      padding-right: 1.75rem;

      &:hover {
        background-color: $white;
        border-color: #c9ccdf;
      }

      &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgb(0 123 255 / 25%);
      }

      &:disabled {
        color: #0f0f0f;
        background-color: #e9ecef;
        opacity: 1;
      }

      &::after {
        display: none;
      }
    }

    .dropdown-menu {
      border: 1px solid #c9ccdf;
      border-radius: $input-radius;
      padding: 0.25rem 0;
      margin-top: 2px;
      min-width: 100%;
    }

    .dropdown-item {
      display: flex;
      align-items: center;
      padding: 0.375rem 0.75rem;
      font-size: $input-font-size;
      color: $input-color;

      &:hover {
        background-color: #f8f9fa;
      }

      &.active,
      &:active {
        background-color: $info;
        color: $white;
      }
    }

    .dropdown-divider {
      margin: 0.25rem 0;
    }

    .rs-select-btn-content {
      min-width: 0;
      overflow: hidden;
    }

    .rs-select-btn-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
